/**
 * Catálogo do site público editado pelo painel (lido pelo site no build e pela tela "Configurações do site").
 *
 * - Configurações (`content.site_settings`, chaves `site.*`, públicas): contatos, redes, marca, logo e SEO.
 *   O valor padrão de cada uma é o que está no repositório do site (apps/site/src/data/site.ts), importado para
 *   o painel por infra/seed/seed_site_content.sql.
 * - Páginas fixas: entradas `kind = page` cujos blocos têm `type = "site_section"`; cada bloco substitui uma
 *   seção da página no build (`key` = nome da seção). Blocos de outro tipo são ignorados pelo site.
 * - Gancho de atualização do site: `site.rebuild_hook` (privada, só por `content.set_rebuild_hook`).
 *
 * Puro: sem dependências, usado pelo site (Node) e pelo painel (navegador).
 */

export type SiteSettingType = "text" | "textarea" | "email" | "url" | "path" | "whatsapp" | "phone" | "number";

export interface SiteSettingField {
  key: string;
  label: string;
  group: "contato" | "redes" | "marca" | "seo";
  type: SiteSettingType;
  help?: string;
  /** Tamanho máximo do texto. */
  max?: number;
}

export const SITE_SECTION_BLOCK = "site_section";
export const REBUILD_HOOK_KEY = "site.rebuild_hook";

export const SITE_SETTING_GROUPS: Record<SiteSettingField["group"], string> = {
  contato: "Contato",
  redes: "Redes e links",
  marca: "Marca e textos fixos",
  seo: "SEO e compartilhamento",
};

export const SITE_SETTING_FIELDS: SiteSettingField[] = [
  { key: "site.whatsapp_number", label: "WhatsApp (só números, com DDI e DDD)", group: "contato", type: "whatsapp", help: "Ex.: 5577991005071. Usado em todos os botões de WhatsApp do site." },
  { key: "site.whatsapp_display", label: "WhatsApp como aparece no site", group: "contato", type: "text", max: 40 },
  { key: "site.whatsapp_message", label: "Mensagem inicial do WhatsApp", group: "contato", type: "text", max: 300 },
  { key: "site.whatsapp_note", label: "Nota abaixo do botão de WhatsApp", group: "contato", type: "text", max: 200 },
  { key: "site.phone", label: "Telefone (link de ligação)", group: "contato", type: "phone", help: "Com DDI: +5577991005071." },
  { key: "site.email", label: "E-mail de contato", group: "contato", type: "email" },
  { key: "site.privacy_email", label: "E-mail de privacidade (LGPD)", group: "contato", type: "email" },
  { key: "site.hours", label: "Horário de atendimento", group: "contato", type: "text", max: 120 },
  { key: "site.city", label: "Cidade", group: "contato", type: "text", max: 120 },
  { key: "site.region", label: "Região atendida", group: "contato", type: "textarea", max: 300 },
  { key: "site.address", label: "Endereço completo (opcional)", group: "contato", type: "text", max: 300, help: "Vai para os dados estruturados (Google). Vazio: só a cidade." },
  { key: "site.social.linkedin", label: "LinkedIn", group: "redes", type: "url" },
  { key: "site.social.instagram_vixio", label: "Instagram da Vixio", group: "redes", type: "url" },
  { key: "site.social.instagram_stenio", label: "Instagram do Stênio", group: "redes", type: "url" },
  { key: "site.social.lattes", label: "Currículo Lattes", group: "redes", type: "url" },
  { key: "site.app_url", label: "Área do cliente (Vixio OS)", group: "redes", type: "url" },
  { key: "site.name", label: "Nome no título das páginas", group: "marca", type: "text", max: 60 },
  { key: "site.tagline", label: "Assinatura da marca", group: "marca", type: "text", max: 120 },
  { key: "site.positioning", label: "Frase de posicionamento", group: "marca", type: "textarea", max: 600 },
  { key: "site.signature", label: "Frase-assinatura (duas frases separadas por ponto)", group: "marca", type: "text", max: 200 },
  { key: "site.conflict", label: "Conflito de interesse declarado (duas frases)", group: "marca", type: "text", max: 300 },
  { key: "site.footer_tagline", label: "Texto do rodapé", group: "marca", type: "textarea", max: 400 },
  { key: "site.logo_dark_url", label: "Logo para fundo escuro (cabeçalho e rodapé)", group: "marca", type: "path", help: "Caminho do site (/brand/...) ou endereço no mesmo domínio (https://vixio.com.br/api/storage/...)." },
  { key: "site.logo_light_url", label: "Logo para fundo claro", group: "marca", type: "path" },
  { key: "site.logo_width", label: "Largura original do logo (px)", group: "marca", type: "number", help: "Com a altura, define a proporção do logo." },
  { key: "site.logo_height", label: "Altura original do logo (px)", group: "marca", type: "number" },
  { key: "site.seo.default_description", label: "Descrição padrão (páginas sem descrição própria)", group: "seo", type: "textarea", max: 300 },
  { key: "site.seo.organization_description", label: "Descrição da organização (dados estruturados)", group: "seo", type: "textarea", max: 600 },
  { key: "site.seo.og_image", label: "Imagem de compartilhamento padrão", group: "seo", type: "path", help: "1200 × 630 px. Caminho do site ou endereço no mesmo domínio." },
  { key: "site.seo.og_image_alt", label: "Texto alternativo da imagem de compartilhamento", group: "seo", type: "text", max: 200 },
  { key: "site.analytics.umami_url", label: "Estatísticas: endereço do Umami (sem cookies)", group: "seo", type: "url", max: 200, help: "Ex.: https://analytics.vixio.com.br. Vazio desliga as estatísticas do site." },
  { key: "site.analytics.umami_website_id", label: "Estatísticas: identificador do site no Umami", group: "seo", type: "text", max: 64, help: "Em Umami → Sites → vixio.com.br → Editar → Website ID." },
];

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Campos que podem ficar vazios (vazio = não usar). */
const OPTIONAL_KEYS = new Set(["site.address", "site.analytics.umami_url", "site.analytics.umami_website_id"]);

/** Valida um valor do catálogo. Devolve a mensagem de erro ou null. */
export function validateSiteSetting(field: SiteSettingField, value: unknown): string | null {
  if (field.type === "number") {
    return typeof value === "number" && Number.isInteger(value) && value > 0 && value <= 10000 ? null : "Informe um número inteiro positivo";
  }
  if (typeof value !== "string") return "Informe um texto";
  const v = value.trim();
  if (OPTIONAL_KEYS.has(field.key) && v === "") return null;
  if (!v) return "Campo obrigatório";
  if (v.length > (field.max ?? 500)) return `No máximo ${field.max ?? 500} caracteres`;
  switch (field.type) {
    case "email":
      return EMAIL.test(v) ? null : "E-mail inválido";
    case "url":
      return /^https?:\/\/[^\s]{3,}$/.test(v) ? null : "Use um endereço completo (https://...)";
    case "path":
      return /^(\/(?!\/)[^\s]*|https:\/\/[^\s]{3,})$/.test(v) ? null : "Use um caminho do site (/...) ou https://";
    case "whatsapp":
      return /^[1-9][0-9]{9,14}$/.test(v) ? null : "Só números, com DDI e DDD (10 a 15 dígitos)";
    case "phone":
      return /^\+?[0-9]{8,15}$/.test(v) ? null : "Só números, com + e DDI";
    default:
      return /[<>]/.test(v) ? "Sem < ou >" : null;
  }
}

export function siteSettingField(key: string): SiteSettingField | undefined {
  return SITE_SETTING_FIELDS.find((f) => f.key === key);
}
